import * as React from 'react';
import { storyblokEditable, type SbBlokData } from '@storyblok/react';

import type { MembersBlockStoryblok } from '../../types/storyblok';
import { MemberCard } from './MemberCard';

interface MembersBlockProps {
  blok: MembersBlockStoryblok;
}

type Variant = 'north_america' | 'others';

/**
 * Normalizes variant value, defaulting to 'others' when not specified.
 */
const normalizeVariant = (value: MembersBlockStoryblok['variant']): Variant =>
  value === 'north_america' ? 'north_america' : 'others';

/**
 * MembersBlock displays member organizations in a responsive grid.
 *
 * Two variants controlled by dropdown:
 * - north_america: Cards with logo, title, divider, and service links list
 * - others: Cards with logo, clickable title, and location subtitle
 *
 * Responsive columns. The card packs a fixed 90px logo beside the text, so the
 * text column is `cardWidth - 138px`. Four columns only leave enough room for a
 * long member name once the grid hits its 1440px max width; below that the
 * column count steps down so names wrap on word boundaries instead of breaking
 * mid-word.
 *
 * >=1440px: 4 columns (card 296px)   >=1280px: 3 columns (card 352px)
 * >=720px:  2 columns                <720px:   1 column
 * Gap: 24px mobile, 32px from 1024px.
 */
export const MembersBlock: React.FC<MembersBlockProps> = ({ blok }) => {
  const variant = normalizeVariant(blok.variant);
  const members = blok.members ?? [];
  const showDivider = blok.show_divider ?? false;

  // Title comes from the blok for all variants (i18n support)
  const sectionTitle = blok.title;

  const headlineId = React.useId();

  return (
    <section
      {...storyblokEditable(blok as unknown as SbBlokData)}
      aria-labelledby={headlineId}
      className="px-4 py-10 desktop:px-grid-margin-desktop desktop:pb-[40px] max-w-site mx-auto"
    >
      {/* Divider line above section */}
      {showDivider && <div className="w-full h-px bg-gray-200 mb-10" aria-hidden="true" />}
      {/* Section title */}
      <h2 id={headlineId} className="text-2xl font-extrabold text-brand-blue mb-6">
        {sectionTitle}
      </h2>

      {/* Members layout - flex for North America (fixed-width cards), grid for others */}
      {members.length > 0 && (
        <div
          className={
            variant === 'north_america'
              ? 'flex flex-col gap-6 desktop:flex-row desktop:gap-8'
              : 'grid grid-cols-1 gap-6 min-[720px]:grid-cols-2 desktop:gap-8 min-[1280px]:grid-cols-3 min-[1440px]:grid-cols-4'
          }
        >
          {members.map((member) => (
            <MemberCard key={member._uid} blok={member} variant={variant} />
          ))}
        </div>
      )}
    </section>
  );
};
